import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { setNavbarCollapsed } from 'dashboard/redux/slices/navbar';
import { RootState } from 'dashboard/redux/store';

interface NavbarToggleCallbacks {
  collapseNavbar: () => void;
  expandNavbar: () => void;
  toggleNavbarCollapsed: () => void;
}

/**
 * useNavbarToggle
 *
 * Effectively the useToggle hook adapted specifically for the
 * dashboard navbar's expanded state in redux.
 *
 * Moving the expanded state to redux allows other dashboard components to
 * dynamically update in response, and for the navbar to be controlled
 * from other dashboard contexts - such as collapsing it upon editor load
 */
export default function (): [boolean, NavbarToggleCallbacks] {
  const dispatch = useDispatch();
  const navbarCollapsed = useSelector<RootState, boolean>((state) => state.navbar.navbarCollapsed);
  const collapseNavbar = useCallback(
    () => dispatch(setNavbarCollapsed({ navbarCollapsed: true })),
    [dispatch],
  );
  const expandNavbar = useCallback(
    () => dispatch(setNavbarCollapsed({ navbarCollapsed: false })),
    [dispatch],
  );
  const toggleNavbarCollapsed = useCallback(() => {
    dispatch(setNavbarCollapsed({ navbarCollapsed: !navbarCollapsed }));
  }, [dispatch, navbarCollapsed]);
  return [navbarCollapsed, { collapseNavbar, expandNavbar, toggleNavbarCollapsed }];
}
